/**
 * AiSettingsPanel — AI-000-G
 *
 * Tenant-admin widget for the AI cost governor. Embedded at the top of
 * TenantConfigAdmin so a tenant admin can:
 *
 *   - Globally enable / disable AI (toggling kicks every surface back to the
 *     rule-based fallback). Useful for cost emergencies and inspection mode.
 *   - Set the daily token budget, the per-minute rate cap, and the hard
 *     USD cap.
 *   - See today's tokens / cost-micros consumed in real time.
 *
 * Reads:  GET  /api/v1/admin/ai-usage/:tenantId
 *         (mock-api uses in-memory state; real services back this with
 *         ai_advisory.tenant_budgets + ai_advisory.advisory_invocations.)
 *
 * Writes: POST /api/v1/admin/ai-budget/:tenantId
 *         body: { aiEnabled, dailyTokenBudget, perMinuteMaxCalls, hardCapUsdMicros }
 */

import { useCallback, useEffect, useState } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';
import { Button, Card, CardBody, CardHeader } from '@edms/design-system';
import styles from './AiSettingsPanel.module.css';

interface AiUsage {
  date?: string;
  tokens?: number;
  costMicros?: number;
  callsThisMinute?: number;
}

interface AiBudget {
  aiEnabled: boolean;
  dailyTokenBudget: number;
  perMinuteMaxCalls: number;
  hardCapUsdMicros: number;
}

const DEFAULT_BUDGET: AiBudget = {
  aiEnabled: true,
  dailyTokenBudget: 1_000_000,
  perMinuteMaxCalls: 60,
  hardCapUsdMicros: 50_000_000,
};

const TENANT_ID = '00000000-0000-0000-0000-000000000001';

function formatUsdMicros(n: number): string {
  if (!Number.isFinite(n)) return '$0.00';
  return `$${(n / 1_000_000).toFixed(2)}`;
}

export function AiSettingsPanel() {
  const [budget, setBudget] = useState<AiBudget>(DEFAULT_BUDGET);
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const fetchUsage = useCallback(async () => {
    try {
      const r = await fetch(apiUrl(`/api/v1/admin/ai-usage/${TENANT_ID}`), { headers: buildHeaders() });
      if (r.ok) setUsage(await r.json());
    } catch { /* tolerate */ }
  }, []);

  useEffect(() => {
    void fetchUsage();
    const id = setInterval(fetchUsage, 30_000);   // refresh every 30s
    return () => clearInterval(id);
  }, [fetchUsage]);

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const r = await fetch(apiUrl(`/api/v1/admin/ai-budget/${TENANT_ID}`), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify(budget),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setFeedback({ kind: 'ok', text: 'AI budget saved' });
    } catch (err) {
      setFeedback({ kind: 'err', text: `save failed: ${(err as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const usagePct = usage?.tokens
    ? Math.min(100, Math.round((usage.tokens / budget.dailyTokenBudget) * 100))
    : 0;
  const costPct = usage?.costMicros
    ? Math.min(100, Math.round((usage.costMicros / budget.hardCapUsdMicros) * 100))
    : 0;

  return (
    <Card variant="elevated" className={styles.panel}>
      <CardHeader>
        <div className={styles.headerRow}>
          <h2 className={styles.title}>AI cost governor</h2>
          <span className={budget.aiEnabled ? styles.statusOn : styles.statusOff}>
            {budget.aiEnabled ? 'ENABLED' : 'DISABLED'}
          </span>
        </div>
      </CardHeader>
      <CardBody>
        <p className={styles.subtitle}>
          Toggling <strong>AI Enabled</strong> instantly switches every advisory surface to the
          rule-based fallback — useful for cost emergencies, audits, or inspector sessions.
        </p>

        <div className={styles.row}>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={budget.aiEnabled}
              onChange={(e) => setBudget((b) => ({ ...b, aiEnabled: e.target.checked }))}
            />
            <span>AI Enabled (per-tenant master switch)</span>
          </label>
        </div>

        <div className={styles.grid}>
          <NumberInput
            label="Daily token budget"
            value={budget.dailyTokenBudget}
            onChange={(v) => setBudget((b) => ({ ...b, dailyTokenBudget: v }))}
          />
          <NumberInput
            label="Per-minute max calls"
            value={budget.perMinuteMaxCalls}
            onChange={(v) => setBudget((b) => ({ ...b, perMinuteMaxCalls: v }))}
          />
          <NumberInput
            label="Hard USD cap (micros)"
            value={budget.hardCapUsdMicros}
            onChange={(v) => setBudget((b) => ({ ...b, hardCapUsdMicros: v }))}
            hint={`= ${formatUsdMicros(budget.hardCapUsdMicros)}/day`}
          />
        </div>

        {usage && (
          <div className={styles.usage}>
            <h3 className={styles.usageTitle}>Today’s usage</h3>
            <div className={styles.usageRow}>
              <span>Tokens</span>
              <Progress pct={usagePct} />
              <span>{(usage.tokens || 0).toLocaleString()} / {budget.dailyTokenBudget.toLocaleString()}</span>
            </div>
            <div className={styles.usageRow}>
              <span>Cost</span>
              <Progress pct={costPct} variant={costPct > 80 ? 'warn' : 'ok'} />
              <span>{formatUsdMicros(usage.costMicros || 0)} / {formatUsdMicros(budget.hardCapUsdMicros)}</span>
            </div>
          </div>
        )}

        {feedback && (
          <p className={feedback.kind === 'ok' ? styles.fbOk : styles.fbErr} role="status">
            {feedback.text}
          </p>
        )}

        <div className={styles.actions}>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save AI budget'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function NumberInput({ label, value, onChange, hint }: { label: string; value: number; onChange: (v: number) => void; hint?: string }) {
  return (
    <label className={styles.numField}>
      <span className={styles.numLabel}>{label}</span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className={styles.numInput}
      />
      {hint && <span className={styles.numHint}>{hint}</span>}
    </label>
  );
}

function Progress({ pct, variant = 'ok' }: { pct: number; variant?: 'ok' | 'warn' }) {
  return (
    <div className={styles.progressTrack} role="meter" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div
        className={`${styles.progressFill} ${variant === 'warn' ? styles.progressFillWarn : ''}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
